/**
 * #1389 — the compact injury signals outside the report itself: one entry per
 * NBA team row on the board, one status per exactly-matched prop player, and
 * the props section's shared report age.
 *
 * A board entry is the disclosure button of its game's inline board report
 * (`BoardInjuryReport`): it opens a read-only report in the board and changes
 * nothing about the Game Detail panel, its ticket, or the aim. A prop status
 * and the props age live on the Game Detail page, beside the game's own
 * Injuries disclosure, and jump to it (stores/injuryDisclosure). None of them
 * opens on its own.
 *
 * Held slots: every entry renders in a fixed-height line whose presence does
 * not depend on the data, so a report arriving, changing or clearing changes
 * text inside the line and never the board's row heights or column widths.
 * The board entry's button fills its slot, so its hit area does not change
 * with its text either (content stays left-aligned).
 *
 * Width (the board's 150px label budget): a 12px icon slot (#1400: 10px read
 * as a smudge), the count tokens with each count shown to at most `9+` (exact
 * counts are in the label and the disclosure), then the age — 5 × `O9+` plus
 * the age is about 142px of 10px monospace, so the worst case fits without
 * clipping. The last-known cue
 * REPLACES the icon (an amber warning triangle) rather than adding width.
 *
 * Each button carries `data-lt2-owner`: a board row the trader has aimed is a
 * `data-lt2-hotkey-control` boundary that hands a bare button's Space to the
 * aimed card (#602), and Space on an injury entry must never run Pull All.
 */
import { Fragment } from 'react';
import { AlertTriangle, Bandage } from 'lucide-react';
import { coreGameStateKey } from '@/lib/gameState/admission';
import {
  injuryCountTokens, propInjuryRow, reportAgeShort, reportAgeWords, teamInjuryState, teamInjuryWords,
  type InjuryStatus, type TeamInjuryState,
} from '@/lib/gameState/injurySignals';
import { useSharedNow } from '@/lib/infra/sharedClock';
import { baseGameKey } from '@/services/analyticsGameKey';
import { useCoreGameStatesStore } from '@/services/coreGameStates';
import { requestInjuryDisclosure } from '@/stores/injuryDisclosure';

/** Minute labels need no faster tick. */
const AGE_TICK_MS = 30_000;

const TONE: Record<InjuryStatus, string> = {
  out: 'text-red-400 light:text-red-600',
  doubtful: 'text-red-400 light:text-red-600',
  questionable: 'text-amber-400 light:text-amber-700',
  probable: 'text-muted-foreground',
  available: 'text-muted-foreground',
};

function useInjuryRow(gameKey: string) {
  const key = coreGameStateKey(baseGameKey(gameKey));
  const configured = useCoreGameStatesStore((s) => s.configured);
  const row = useCoreGameStatesStore((s) => (key ? s.injuries.get(key) : undefined));
  const live = useCoreGameStatesStore((s) => s.status === 'live');
  return { configured, row, live };
}

function StateText({ state }: { state: TeamInjuryState }) {
  if (state.kind === 'unavailable') return <span className="text-muted-foreground">—</span>;
  if (state.kind === 'pending') return <span className="text-amber-400 light:text-amber-700">pending</span>;
  if (state.kind === 'clean') return <span className="text-muted-foreground">none</span>;
  return (
    <span className="inline-flex gap-[3px]">
      {/* Whitespace between flex items is not rendered (no width), but it
          keeps copied text and textContent readable: "O1 D1". */}
      {injuryCountTokens(state.counts).map((t, i) => (
        <Fragment key={t.status}>{i > 0 && ' '}<span className={TONE[t.status]}>{t.letter}{t.count > 9 ? '9+' : t.count}</span></Fragment>
      ))}
    </span>
  );
}

/** The leading slot: the injury mark, or — on last-known data — the visible
 *  warning that replaces it (same 12px, so the cue costs no width). */
function LeadMark({ lastKnown }: { lastKnown: boolean }) {
  return lastKnown
    ? <AlertTriangle size={12} aria-hidden data-testid="injury-last-known" className="shrink-0 text-amber-500 light:text-amber-700" />
    : <Bandage size={12} aria-hidden className="text-muted-foreground shrink-0" />;
}

const ENTRY_CLASS = 'inline-flex min-w-0 items-center gap-1 whitespace-nowrap rounded font-mono text-[10px] leading-4 hover:underline focus-visible:outline focus-visible:outline-1';

/**
 * A board team row's entry: `O1 D1 32m`. The age rides the game's top (away)
 * row only — one per game, beside the local tip time, never a second clock.
 * Activation toggles the game's inline board report (`expanded`/`controls`).
 */
export function BoardInjuryEntry({ gameKey, teamCode, showAge, expanded, controls, onToggle }: {
  gameKey: string;
  teamCode: string | null | undefined;
  showAge: boolean;
  expanded: boolean;
  controls: string;
  onToggle: (origin: HTMLElement) => void;
}) {
  const { configured, row, live } = useInjuryRow(gameKey);
  const now = useSharedNow(AGE_TICK_MS, configured && showAge);
  if (!configured) return null;
  const block = row?.block ?? null;
  const code = teamCode?.toUpperCase() ?? null;
  const state = teamInjuryState(block, code);
  const lastKnown = !!block && (!!row?.invalid || !live);
  const age = showAge && block ? reportAgeShort(block.reportAt, now) : '';
  const label = `${code ?? 'Team'} injury report: ${teamInjuryWords(state)}`
    + (block ? `; report ${reportAgeWords(block.reportAt, now)}` : '')
    + (lastKnown ? '; last known, connection not live' : '')
    + (expanded ? '. Injury report open' : '. Show injury report');
  return (
    <div className="flex h-4 min-w-0 items-center" data-testid="board-injury-line">
      <button
        type="button"
        data-lt2-owner=""
        data-testid="board-injury-entry"
        data-injury-state={state.kind}
        aria-label={label}
        aria-expanded={expanded}
        aria-controls={controls}
        title={label}
        className={`${ENTRY_CLASS} w-full justify-start`}
        onClick={(e) => {
          // The row's own click toggles expansion and seeds a ticket side.
          e.stopPropagation();
          onToggle(e.currentTarget);
        }}
      >
        <LeadMark lastKnown={lastKnown} />
        <StateText state={state} />
        {age && <>{' '}<span className="text-muted-foreground ml-0.5">{age}</span></>}
      </button>
    </div>
  );
}

/** A prop player's status, shown only for an exact wire-playerKey match. */
export function PropInjuryStatus({ gameKey, wirePlayerKey }: { gameKey: string; wirePlayerKey: string | null | undefined }) {
  const { configured, row, live } = useInjuryRow(gameKey);
  if (!configured) return null;
  const block = row?.block ?? null;
  const hit = propInjuryRow(block, wirePlayerKey);
  const lastKnown = !!block && (!!row?.invalid || !live);
  return (
    // #1400: a 2px gap below the player label (the status read as part of
    // it). Held whether or not the player is listed, so a report arriving or
    // clearing never moves the prop rows.
    <div className="mt-0.5 flex h-4 min-w-0 items-center" data-testid="prop-injury-line">
      {hit && block && (
        <button
          type="button"
          data-lt2-owner=""
          data-testid="prop-injury-status"
          aria-label={`${hit.player} injury report: ${hit.statusRaw}${hit.reason ? `, ${hit.reason}` : ''}${lastKnown ? '; last known, connection not live' : ''}. Open injury details`}
          title={hit.reason ? `${hit.statusRaw} — ${hit.reason}` : hit.statusRaw}
          className={ENTRY_CLASS}
          onClick={(e) => {
            e.stopPropagation();
            requestInjuryDisclosure(gameKey, { playerKey: hit.playerKey ?? null, team: hit.team, origin: e.currentTarget });
          }}
        >
          <LeadMark lastKnown={lastKnown} />
          <span className={TONE[hit.status]}>{hit.statusRaw}</span>
        </button>
      )}
    </div>
  );
}

/**
 * The props section's one shared report age, shown while any visible player
 * has an exact-match status (`wirePlayerKeys` = the visible ladders' wire keys).
 */
export function PropsInjuryReportAge({ gameKey, wirePlayerKeys }: { gameKey: string; wirePlayerKeys: readonly (string | null | undefined)[] }) {
  const { configured, row, live } = useInjuryRow(gameKey);
  const block = row?.block ?? null;
  const lastKnown = !!block && (!!row?.invalid || !live);
  const shown = configured && !!block && wirePlayerKeys.some((k) => propInjuryRow(block, k) != null);
  const now = useSharedNow(AGE_TICK_MS, shown);
  if (!shown || !block) return null;
  return (
    <button
      type="button"
      data-lt2-owner=""
      data-testid="props-injury-age"
      className="text-muted-foreground inline-flex min-w-0 shrink items-center gap-1 truncate text-xs hover:underline"
      aria-label={`Injury report ${reportAgeWords(block.reportAt, now)}${lastKnown ? '; last known, connection not live' : ''}. Open injury details`}
      onClick={(e) => requestInjuryDisclosure(gameKey, { origin: e.currentTarget })}
    >
      {lastKnown && <AlertTriangle size={11} aria-hidden data-testid="injury-last-known" className="shrink-0 text-amber-500 light:text-amber-700" />}
      Injury report {reportAgeShort(block.reportAt, now)}{lastKnown ? ' · last known' : ''}
    </button>
  );
}
